import { Button, ButtonSize, ButtonVariant, IconVariants, Input, Pagination, Tabs, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { useCallback, useMemo, useState } from "react";

import { Col, Row } from "@/components/customComponents";
import { CHART_PAGE_SIZE } from "@/pages/CreatorAnalytics/constants";
import type { ICharacterPerformance } from "@/pages/CreatorAnalytics/types";
import { exportCharactersCsv } from "@/pages/CreatorAnalytics/utils";

import CharactersTable from "./CharactersTable";

const FIRST_PAGE = 1;
const ROOT_TEST_ID = "TopCharactersTable";

type SortKey = "totalRequests" | "avgDepth" | "avgRating" | "netLikes" | "uniqueUsers";

const SORT_TABS: { name: SortKey; label: string }[] = [
  { name: "totalRequests", label: "Requests" },
  { name: "uniqueUsers", label: "Users" },
  { name: "avgDepth", label: "Depth" },
  { name: "avgRating", label: "Rating" },
  { name: "netLikes", label: "Net Likes" },
];

// TODO(igor-nextday): [NX-7407] move user-facing labels to Namespace.CreatorAnalytics i18n keys.
const TITLE_LABEL = "Your Chatbots";
const EXPORT_LABEL = "Export CSV";
const SORT_LABEL = "Sort by:";
const SEARCH_PLACEHOLDER = "Search by name or tag";
const NO_RESULTS_LABEL = "No chatbots match your search.";
const HINT_LABEL = "Click any chatbot row to see detailed performance over time";

const matchesSearch = (character: ICharacterPerformance, normalizedSearch: string): boolean => {
  if (!normalizedSearch) {
    return true;
  }
  if (character.name.toLowerCase().includes(normalizedSearch)) {
    return true;
  }
  // Tags use startsWith so a query like "male" doesn't return Female-tagged
  // bots just because "female" happens to contain "male" as a substring.
  return character.tags.some((tag) => tag.toLowerCase().startsWith(normalizedSearch));
};

interface ITopCharactersTableProps {
  characters: ICharacterPerformance[];
  onSelectCharacter?: (character: ICharacterPerformance) => void;
  "data-testid"?: string;
}

const TopCharactersTable = ({ characters, onSelectCharacter, "data-testid": testId = ROOT_TEST_ID }: ITopCharactersTableProps) => {
  const [stateSortBy, setStateSortBy] = useState<SortKey>("totalRequests");
  const [statePage, setStatePage] = useState(FIRST_PAGE);
  const [stateSearchQuery, setStateSearchQuery] = useState("");

  const filtered = useMemo(() => {
    const normalizedSearch = stateSearchQuery.trim().toLowerCase();
    if (!normalizedSearch) {
      return characters;
    }
    return characters.filter((character) => matchesSearch(character, normalizedSearch));
  }, [characters, stateSearchQuery]);

  const sorted = useMemo(
    () =>
      [...filtered].sort((itemA, itemB) => {
        const valA = itemA[stateSortBy] ?? 0;
        const valB = itemB[stateSortBy] ?? 0;
        return (valB as number) - (valA as number);
      }),
    [filtered, stateSortBy],
  );

  const totalPages = Math.max(FIRST_PAGE, Math.ceil(sorted.length / CHART_PAGE_SIZE));
  const safePage = Math.min(statePage, totalPages);
  const paginatedRows = useMemo(() => sorted.slice((safePage - 1) * CHART_PAGE_SIZE, safePage * CHART_PAGE_SIZE), [sorted, safePage]);
  const rankOffset = (safePage - 1) * CHART_PAGE_SIZE;
  const hasResults = sorted.length > 0;

  const handleSortChange = useCallback((tabName: string) => {
    setStateSortBy(tabName as SortKey);
    setStatePage(FIRST_PAGE);
  }, []);

  const handlePageChange = useCallback((page: number) => setStatePage(page), []);

  const handleSearchChange = useCallback((value: string) => {
    setStateSearchQuery(value);
    setStatePage(FIRST_PAGE);
  }, []);

  const handleSearchClear = useCallback(() => handleSearchChange(""), [handleSearchChange]);

  const handleExportCsv = useCallback(() => exportCharactersCsv(sorted), [sorted]);

  return (
    <Col className="gap-4" data-testid={testId}>
      <Row className="flex-wrap items-center gap-2">
        <Text variant={TextVariants.HEADING_6_BOLD}>{TITLE_LABEL}</Text>
        <Button
          data-testid="TopCharactersTable-ExportButton"
          size={ButtonSize.SMALL}
          variant={ButtonVariant.SECONDARY}
          iconStart={IconVariants.DOWNLOAD}
          onClick={handleExportCsv}
          className="ml-1"
        >
          {EXPORT_LABEL}
        </Button>
        <Row className="ml-auto items-center gap-2 overflow-x-auto">
          <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-default-500">
            {SORT_LABEL}
          </Text>
          <Tabs tabItems={SORT_TABS} activeTab={stateSortBy} onTabChange={handleSortChange} />
        </Row>
      </Row>

      <Input
        data-testid="TopCharactersTable-SearchInput"
        placeholder={SEARCH_PLACEHOLDER}
        value={stateSearchQuery}
        iconStart={IconVariants.SEARCH}
        isClearable
        onChange={handleSearchChange}
        onClear={handleSearchClear}
      />

      {hasResults ? (
        <CharactersTable rows={paginatedRows} rankOffset={rankOffset} onSelectCharacter={onSelectCharacter} />
      ) : (
        <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="py-6 text-center text-default-400">
          {NO_RESULTS_LABEL}
        </Text>
      )}

      {totalPages > FIRST_PAGE ? (
        <Row className="justify-center">
          <Pagination count={totalPages} page={safePage} onChange={handlePageChange} />
        </Row>
      ) : null}

      {onSelectCharacter && hasResults ? (
        <Text variant={TextVariants.LABEL_XSMALL_REGULAR} className="text-center text-default-400">
          {HINT_LABEL}
        </Text>
      ) : null}
    </Col>
  );
};

export default TopCharactersTable;
